import React, { CSSProperties } from 'react';
import { Autocomplete, Box, Link as MuiLink, SxProps, TextField, Theme } from '@mui/material';
import { Link } from 'react-router-dom';
import { TeamObjectType } from '../../generated/schemas';
import { TeamLogoComponent } from '../logos';

export type TeamOption = Pick<TeamObjectType, 'id' | 'name' | 'styles'>;

type TeamAutocompleteProps = {
  options: TeamOption[];
  value: TeamOption | null;
  onChange: (team: TeamOption | null) => void;
  // Only set where an option should be a real link to somewhere (e.g. that team's page) -- lets a
  // user ctrl/cmd+click, middle-click, or right-click "open in new tab" an option without disturbing
  // the current selection. Omit for pure form-field usage, where there's nowhere for a link to go.
  getOptionHref?: (team: TeamOption) => string;
  disableClearable?: boolean;
  placeholder?: string;
  sx?: SxProps<Theme>;
  // Extra styles for the typed text itself, layered over the global input baseline from theme.ts.
  // Plain CSS properties only (not the full `sx` shorthand/theme-callback union) since it's applied
  // directly to `.MuiInputBase-input`.
  inputSx?: CSSProperties;
};

export function TeamAutocomplete({
  options,
  value,
  onChange,
  getOptionHref,
  disableClearable,
  placeholder = 'Select a team',
  sx,
  inputSx,
}: TeamAutocompleteProps) {
  return (
    <Autocomplete<TeamOption, false, boolean, false>
      size="small"
      autoHighlight
      disableClearable={disableClearable}
      options={options}
      value={value}
      onChange={(_, option) => onChange(option)}
      getOptionLabel={team => team.name}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      sx={sx}
      renderOption={(props, team) => {
        const label = (
          <>
            <TeamLogoComponent styles={team.styles} fallbackToBox sx={{ height: 24, width: 24 }} />
            {team.name}
          </>
        );
        const href = getOptionHref?.(team);
        return (
          <Box component="li" {...props} key={team.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {href ? (
              <MuiLink
                component={Link}
                to={href}
                // Kept out of normal Tab order: options are navigated by arrow key (via
                // aria-activedescendant), not by tabbing to each one individually -- only the input
                // itself should be a real Tab stop.
                tabIndex={-1}
                onClick={event => {
                  if (event.metaKey || event.ctrlKey || event.shiftKey) {
                    // Let the browser open this link in a new tab/window natively, and stop the click
                    // from reaching the <li> below -- otherwise Autocomplete's own click handling would
                    // ALSO fire onChange and navigate the CURRENT tab, which isn't what this click means.
                    event.stopPropagation();
                    return;
                  }
                  // A plain click should feel like an in-app selection, not a full-page reload: swallow
                  // the anchor's native navigation and let the click bubble up to the <li>, whose
                  // onClick (wired by Autocomplete) fires onChange and drives the normal client-side
                  // transition. (Middle-click needs no handling here at all -- it fires `auxclick`, not
                  // `click`, so this handler and the <li>'s never run, and the browser's native
                  // background-tab behavior for a real href just happens.)
                  event.preventDefault();
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                  color: 'inherit',
                  textDecoration: 'none',
                }}
              >
                {label}
              </MuiLink>
            ) : (
              label
            )}
          </Box>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={placeholder}
          sx={inputSx && { '& .MuiInputBase-input': inputSx }}
          InputProps={{
            ...params.InputProps,
            startAdornment: value && (
              <TeamLogoComponent styles={value.styles} fallbackToBox sx={{ height: 24, width: 24, ml: 0.5 }} />
            ),
          }}
        />
      )}
    />
  );
}
